import { traceImportAwait } from '@affine/nbstore';
import type { ExtensionType, Workspace } from '@blocksuite/affine/store';
import type { LocalPublicationReceipt } from '@streamlain/doc-publication-contract';

import {
  connectDatabaseImportProducer,
  disposeDatabaseImportWorkerRun,
  startCsvImportInWorker,
  startExcelImportInWorker,
} from './database-import-parser-worker-client';
import type { DatabaseImportSyncProgress } from './database-import-sync-progress';
import {
  type LocalShardedDatabaseImportProgressChange,
  throwIfLocalDatabaseImportAborted,
} from './local-sharded-importer';
import { importShardedDatabasesInWorker } from './worker-sharded-importer';

export type DatabaseImportLocalFirstResult = {
  databaseBlockIds: string[];
  docId: string;
  stats: {
    databaseCount: number;
    totalRows: number;
  };
  /** Creation and all locally committed row groups, including rotated shards. */
  receipts: readonly LocalPublicationReceipt[];
};

export type CsvDatabaseImportLocalFirstOptions = {
  docId: string;
  files: File[];
  hasHeader?: boolean;
  titleColumnIndex?: number;
  dropPosition?: { x: number; y: number };
  collection: Workspace;
  extensions?: ExtensionType[];
  docName?: string;
  estimatedTotalRows?: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
};

export type ExcelDatabaseImportLocalFirstOptions = {
  docId: string;
  files: File[];
  selectedSheets?: Map<string, string[]>;
  hasHeader?: boolean;
  titleColumnIndex?: number;
  dropPosition?: { x: number; y: number };
  collection: Workspace;
  extensions?: ExtensionType[];
  docName?: string;
  estimatedTotalRows?: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  syncProgress?: DatabaseImportSyncProgress;
  signal?: AbortSignal;
};

function selectedSheetsByFileName(
  selectedSheets?: Map<string, string[]>
): Record<string, string[]> | undefined {
  if (!selectedSheets || selectedSheets.size === 0) return undefined;
  return Object.fromEntries(selectedSheets.entries());
}

export async function importCsvFilesLocalFirst(
  options: CsvDatabaseImportLocalFirstOptions
): Promise<DatabaseImportLocalFirstResult> {
  let workerRunId: string | null = null;
  try {
    options.onProgress?.({
      phase: 'csv-reading',
      processedRows: 0,
      totalRows: options.estimatedTotalRows ?? 0,
      percent: null,
    });
    throwIfLocalDatabaseImportAborted(options.signal);
    const workerRun = await startCsvImportInWorker(
      {
        files: options.files,
        hasHeader: options.hasHeader,
        titleColumnIndex: options.titleColumnIndex,
        dropPosition: options.dropPosition,
      },
      options.signal
    );
    workerRunId = workerRun.runId;
    throwIfLocalDatabaseImportAborted(options.signal);

    const result = await importShardedDatabasesInWorker({
      collection: options.collection,
      docId: options.docId,
      sources: workerRun.sources,
      extensions: options.extensions,
      docName: options.docName,
      onProgress: options.onProgress,
      syncProgress: options.syncProgress,
      signal: options.signal,
      input: {
        kind: 'port',
        port: await connectDatabaseImportProducer(
          workerRun.runId,
          options.signal
        ),
      },
    });

    return {
      databaseBlockIds: result.databaseBlockIds,
      docId: result.docId,
      stats: result.stats,
      receipts: result.receipts,
    };
  } finally {
    if (workerRunId) {
      await traceImportAwait('import.parser-dispose', undefined, () =>
        disposeDatabaseImportWorkerRun(workerRunId!)
      ).catch(() => {});
    }
  }
}

export async function importExcelFilesLocalFirst(
  options: ExcelDatabaseImportLocalFirstOptions
): Promise<DatabaseImportLocalFirstResult> {
  let workerRunId: string | null = null;
  try {
    options.onProgress?.({
      phase: 'excel-reading',
      processedRows: 0,
      totalRows: options.estimatedTotalRows ?? 0,
      percent: null,
    });
    throwIfLocalDatabaseImportAborted(options.signal);
    const workerRun = await startExcelImportInWorker(
      {
        files: options.files,
        selectedSheetsByFileName: selectedSheetsByFileName(
          options.selectedSheets
        ),
        hasHeader: options.hasHeader,
        titleColumnIndex: options.titleColumnIndex,
        dropPosition: options.dropPosition,
      },
      options.signal
    );
    workerRunId = workerRun.runId;
    throwIfLocalDatabaseImportAborted(options.signal);

    const result = await importShardedDatabasesInWorker({
      collection: options.collection,
      docId: options.docId,
      sources: workerRun.sources,
      extensions: options.extensions,
      docName: options.docName,
      onProgress: options.onProgress,
      syncProgress: options.syncProgress,
      signal: options.signal,
      input: {
        kind: 'port',
        port: await connectDatabaseImportProducer(
          workerRun.runId,
          options.signal
        ),
      },
    });

    return {
      databaseBlockIds: result.databaseBlockIds,
      docId: result.docId,
      stats: result.stats,
      receipts: result.receipts,
    };
  } finally {
    if (workerRunId) {
      await traceImportAwait('import.parser-dispose', undefined, () =>
        disposeDatabaseImportWorkerRun(workerRunId!)
      ).catch(() => {});
    }
  }
}
